import {useState} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {Button} from '@/components';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import type {BusinessCountry} from '@/types';

import {
  BUSINESS_COUNTRY_ACCOUNT_FLAGS,
  BUSINESS_COUNTRY_CORRIDOR_FLAGS,
  BUSINESS_SURFACES,
  resolveSurfaceFlag,
} from './businessCountryFlags';
import BusinessCountryFlagsModal from './BusinessCountryFlagsModal';

/**
 * "Not set" is distinct from "No": an unset flag is what a country that has
 * never been configured carries, and collapsing the two would make an
 * untouched corridor look deliberately closed.
 */
const formatFlag = (value?: boolean): string => {
  if (value === undefined) {
    return 'Not set';
  }
  return value ? 'Yes' : 'No';
};

type BusinessCountrySettingsProps = {
  handleSave: (id: string, body: Partial<BusinessCountry>) => Promise<void>;
  item: BusinessCountry;
};

/**
 * Settings -> Business Countries -> Manage -> Onboarding.
 *
 * The only editable part of a business country today. `meta.codes` is
 * reference data seeded with the country, and `meta.kyc`/`meta.kyb` are the
 * per-document capture configuration — a form of their own, not a checkbox
 * list, so they are shown read-only on the General tab instead.
 */
const BusinessCountrySettings = ({
  item,
  handleSave,
}: BusinessCountrySettingsProps) => {
  const [showModal, setShowModal] = useState(false);
  const flags = item?.meta?.flags;

  return (
    <>
      <div className="flex justify-end mb-6">
        <PermissionsProvider
          permission={AfriexAdminPermission.BusinessCountryUpdate}
        >
          <Button variant="solid" onClick={() => setShowModal(true)}>
            Update Onboarding Flags
          </Button>
        </PermissionsProvider>
      </div>

      <div className="flex flex-col gap-3">
        <div className="overflow-x-auto">
          {/*
            One row per switch, one column per surface, because the two are
            independent: a corridor closed to direct API traffic stays open in
            the dashboard. Shows the RESOLVED value, flat-key fallback
            included, so this reads as what the server would act on rather
            than as what happens to be stored.
          */}
          <div className="mb-2 py-2 px-3 flex items-center gap-3 justify-between">
            <p className="text-slate-500 dark:text-slate-100 text-sm">&nbsp;</p>
            <div className="flex w-1/2 gap-3">
              {BUSINESS_SURFACES.map(({key, label}) => (
                <p
                  key={key}
                  className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 flex-1"
                >
                  {label}
                </p>
              ))}
            </div>
          </div>

          {BUSINESS_COUNTRY_CORRIDOR_FLAGS.map(({key, label}) => (
            <div
              key={key}
              className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between"
            >
              <p className="text-slate-500 dark:text-slate-100 text-sm">
                {label}
              </p>
              <div className="flex w-1/2 gap-3">
                {BUSINESS_SURFACES.map(({key: surface}) => (
                  <p
                    key={surface}
                    className="text-sm font-medium text-gray-700 dark:text-gray-100 flex-1"
                  >
                    {formatFlag(resolveSurfaceFlag(flags, surface, key))}
                  </p>
                ))}
              </div>
            </div>
          ))}

          {BUSINESS_COUNTRY_ACCOUNT_FLAGS.map(({key, label}) => (
            <div
              key={key}
              className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between"
            >
              <p className="text-slate-500 dark:text-slate-100 text-sm">
                {label}
              </p>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-100 w-1/2">
                {formatFlag(flags?.[key])}
              </p>
            </div>
          ))}
        </div>
      </div>

      <BusinessCountryFlagsModal
        onClose={() => setShowModal(false)}
        showModal={showModal}
        handleSave={handleSave}
        item={item}
      />
    </>
  );
};

export default BusinessCountrySettings;
